import React from 'react';
import { Tooltip } from 'antd';
import moment from 'moment';
import { Calendar, Activity, Clock, FileText, ArrowRight } from 'lucide-react';

import {
  formatNumber2SigFig,
  formatWholeNumber,
} from '../../../../../../../core/utils/campaigns';
import KpiBar, { KpiCard } from '../../../../../../shared/designSystem/components/KpiBar';
import { formatDateOnly } from '../../../../../../shared/designSystem/utils/dateFormat';
import Spinner from '../../../../../../shared/designSystem/components/Spinner';
import { SESSIONS_TOOLTIP } from '../../../../../../shared/constants/tooltips';

// =============================================================================
// DetailBar (V2 Sessions) — v2 KPI strip. Mirrors V1 Sessions DetailBar:
// shared `KpiBar` + `KpiCard` primitives, Lucide icons, flight-dates
// progress card. Identical prop shape.
// =============================================================================

const secondsToTimeText = (seconds) => {
  if (seconds < 0) return 'Invalid time';

  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts = [];

  if (hrs > 0) parts.push(`${hrs}h`);
  if (mins > 0) parts.push(`${mins}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(' ');
};

const FlightDatesCard = ({ startDate, endDate, progressPercent }) => (
  <div className="campaign-overview__kpi">
    <div className="campaign-overview__kpi-head">
      <span className="campaign-overview__kpi-icon campaign-overview__kpi-icon--neutral">
        <Calendar size={16} />
      </span>
      <Tooltip
        overlayClassName="mjl-ds"
        title="Flight Dates refers to the Start & End Dates of the individual campaign"
      >
        <span className="campaign-overview__kpi-eyebrow">Dates</span>
      </Tooltip>
    </div>
    <div className="campaign-overview__flight">
      <div className="campaign-overview__flight-dates">
        <span>{formatDateOnly(startDate, '—')}</span>
        <ArrowRight size={14} className="campaign-overview__flight-arrow" />
        <span>{formatDateOnly(endDate, '—')}</span>
      </div>
      <div className="campaign-overview__flight-progress">
        <div
          className="campaign-overview__flight-progress-bar"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      <div className="campaign-overview__flight-meta">
        {progressPercent}% elapsed
      </div>
    </div>
  </div>
);

const DetailBar = ({
  startDate,
  endDate,
  sessions,
  averageSessionDuration,
  pagesPerSession,
  loading = false,
}) => {
  const getProgress = () => {
    const a = moment(startDate);
    const b = moment(endDate);
    const totaldays = b.diff(a, 'days');
    const progressDays = moment().diff(moment(startDate), 'days');
    if (progressDays <= 0) return 100;
    const daysleft = totaldays - progressDays;
    const daysin = totaldays - daysleft;
    return Math.floor((daysin / totaldays) * 100);
  };

  const currentposition = getProgress();

  return (
    <Spinner spinning={loading}>
      <KpiBar>
        <FlightDatesCard
          startDate={startDate}
          endDate={endDate}
          progressPercent={currentposition}
        />
        <KpiCard
          icon={<Activity size={16} />}
          iconTint="primary"
          label="Sessions"
          labelTooltip={SESSIONS_TOOLTIP}
          value={formatWholeNumber(sessions ? sessions : 0)}
        />
        <KpiCard
          icon={<Clock size={16} />}
          iconTint="emerald"
          label="Avg. Session Duration"
          value={secondsToTimeText(
            formatWholeNumber(averageSessionDuration ? averageSessionDuration : 0)
          )}
        />
        <KpiCard
          icon={<FileText size={16} />}
          iconTint="indigo"
          label="Pages Per Session"
          value={formatNumber2SigFig(pagesPerSession ? pagesPerSession : 0)}
        />
      </KpiBar>
    </Spinner>
  );
};

export default DetailBar;
